import { create } from 'zustand'

import type {
  DirectorAudioClip,
  DirectorParams,
  DirectorSelection,
  DirectorUnit
} from './types'

/**
 * Read-only snapshot of the legacy director console's internal state.
 *
 * The legacy `initDirectorConsole` calls `setSnapshot(partial)` at a handful
 * of mutation hot-spots; React extension components subscribe to this store
 * via `useDirectorBridgeStore` to react to selection / params / clips
 * changes without owning the source of truth.
 */
export interface DirectorBridgeActions {
  compactStoryGaps: () => void
  /**
   * Append one or more images to the story timeline. Used by the React
   * Story Board / Crop flow to hand its split frames back to the legacy
   * timeline implementation.
   *
   * `prompt` may be:
   *   - a single string applied to every unit, or
   *   - an array of strings aligned with `urls` (per-frame prompts from the
   *     language-model enhance API; missing slots fall back to empty string).
   */
  addStoryUnits: (urls: string[], prompt?: string | string[]) => void
  /**
   * Append images immediately with each unit marked `promptPending: true`,
   * returning the newly created unit IDs in order. The caller then kicks off
   * the language-model enhance API in the background and resolves the prompts
   * via `setStoryUnitPrompts` when ready. Used by the Story Board
   * "Add to timeline" flow so images appear instantly while prompts generate.
   */
  addStoryUnitsPending: (urls: string[]) => string[]
  /**
   * Resolve the pending prompts for units created via `addStoryUnitsPending`.
   * `ids[i]` receives `prompts[i]`; the `promptPending` flag is cleared and
   * the prompt panel is refreshed if the selected unit is among them.
   */
  setStoryUnitPrompts: (ids: string[], prompts: string[]) => void
}

export interface DirectorBridgeState {
  isOpen: boolean
  params: DirectorParams | null
  units: DirectorUnit[]
  audioClips: DirectorAudioClip[]
  selection: DirectorSelection | null
  currentTime: number
  zoom: number
  totalSeconds: number
  /** In-flight media uploads (image / video / audio). Apply stays disabled while > 0. */
  pendingUploadCount: number
  actions: DirectorBridgeActions | null
  setSnapshot: (
    partial: Partial<
      Omit<
        DirectorBridgeState,
        'setSnapshot' | 'reset' | 'registerActions' | 'beginUpload' | 'endUpload'
      >
    >
  ) => void
  registerActions: (actions: DirectorBridgeActions) => void
  beginUpload: () => void
  endUpload: () => void
  reset: () => void
}

const initialBridgeState: Omit<
  DirectorBridgeState,
  'setSnapshot' | 'reset' | 'registerActions' | 'beginUpload' | 'endUpload'
> = {
  isOpen: false,
  params: null,
  units: [],
  audioClips: [],
  selection: null,
  currentTime: 0,
  zoom: 1,
  totalSeconds: 10,
  actions: null,
  pendingUploadCount: 0
}

export const useDirectorBridgeStore = create<DirectorBridgeState>(set => ({
  ...initialBridgeState,
  setSnapshot: partial => set(partial),
  registerActions: actions => set({ actions }),
  beginUpload: () =>
    set(state => ({ pendingUploadCount: state.pendingUploadCount + 1 })),
  endUpload: () =>
    set(state => ({
      pendingUploadCount: Math.max(0, state.pendingUploadCount - 1)
    })),
  reset: () => set(initialBridgeState)
}))

export const selectSelectedUnit = (
  state: DirectorBridgeState
): DirectorUnit | null => {
  if (state.selection?.type !== 'story') return null
  return state.units.find(u => u.id === state.selection!.id) ?? null
}

export const selectSelectedAudioClip = (
  state: DirectorBridgeState
): DirectorAudioClip | null => {
  if (state.selection?.type !== 'audio') return null
  return state.audioClips.find(a => a.id === state.selection!.id) ?? null
}